import { Duration } from "@shared/types/duration"

const RELOADED_AT_KEY = "vite-preload-error-reloaded-at"

/** A preload error this soon after the last reload won't be fixed by another one */
const RELOAD_COOLDOWN = Duration.minutes(1).milliseconds

/**
 * How long a page may stay after asking for a reload before we take it that the reload won't
 * happen, e.g. because the user stayed when `useUnsavedChanges` asked whether to leave the page
 */
const RELOAD_CANCELLED_AFTER = Duration.seconds(5).milliseconds

let reloadCancelled: Promise<void> | undefined

/**
 * Reloads the page after Vite failed to load part of it, so the user gets the latest release and a
 * fresh attempt at loading its chunks. Returns false instead when it already did so within the
 * cooldown, which stops a reload loop when a chunk can't be loaded at all.
 */
export const reloadAfterPreloadError = (): boolean => {
  const reloadedAt = Number(sessionStorage.getItem(RELOADED_AT_KEY) || 0)
  if (Date.now() - reloadedAt < RELOAD_COOLDOWN) {
    return false
  }

  console.warn("Vite preload error detected. Attempting to reload the page...")
  sessionStorage.setItem(RELOADED_AT_KEY, `${Date.now()}`)
  reloadCancelled = new Promise((resolve) =>
    setTimeout(() => {
      reloadCancelled = undefined
      resolve()
    }, RELOAD_CANCELLED_AFTER)
  )
  window.location.reload()
  return true
}

/**
 * While {@link reloadAfterPreloadError} reloads the page, resolves once that reload turns out not to
 * happen; `undefined` otherwise.
 */
export const whenPreloadErrorReloadCancelled = (): Promise<void> | undefined => reloadCancelled
